import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore, type AuthSnapshot } from "@/modules/auth/store/auth.store";

/**
 * Axios klient — Prism OS API
 *
 * - `withCredentials: true` — refresh httpOnly cookie yuborish uchun
 * - Request interceptor — `Authorization: Bearer <accessToken>` qo'shadi
 * - Response interceptor — 401 → /auth/refresh → original requestni replay
 * - Refresh queue — bir vaqtning o'zida ko'p 401 kelsa bitta refresh oqar
 */

const baseURL = import.meta.env.VITE_API_URL ?? "/api";

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ----- Request interceptor -----
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// ----- Refresh queue -----
let refreshPromise: Promise<string | null> | null = null;

interface RefreshSuccess {
  accessToken: string;
  accessTokenExpiresAt?: string | null;
  user?: AuthSnapshot["user"];
  tenant?: AuthSnapshot["currentTenant"];
  tenants?: AuthSnapshot["tenants"];
}

/**
 * Refresh oqimi — cookie orqali yangi access oladi.
 * Backend kontraktiga ko'ra javob `LoginResponse` shape'iga teng:
 * `{ accessToken, accessTokenExpiresAt, user, tenant, tenants }`
 */
async function performRefresh(): Promise<string | null> {
  try {
    const res = await axios.post<RefreshSuccess>(
      `${baseURL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const data = res.data;
    if (!data?.accessToken) return null;

    const store = useAuthStore.getState();
    if (data.user && data.tenant && data.tenants) {
      store.setAuth({
        accessToken: data.accessToken,
        accessTokenExpiresAt: data.accessTokenExpiresAt ?? null,
        user: data.user,
        currentTenant: data.tenant,
        tenants: data.tenants,
      });
    } else {
      // Faqat token yangilangan bo'lsa
      store.setAccessToken(data.accessToken, data.accessTokenExpiresAt ?? null);
    }
    return data.accessToken;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

function getOrCreateRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Boot vaqtida bitta marta refresh chaqirish.
 * `App.tsx`'dagi AuthGuard ishlatadi.
 */
export async function bootstrapAuth(): Promise<boolean> {
  const store = useAuthStore.getState();
  store.setHydrating(true);
  const token = await getOrCreateRefresh();
  store.setHydrating(false);
  return Boolean(token);
}

// ----- Response interceptor -----
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as
      | (AxiosRequestConfig & { _retry?: boolean; url?: string })
      | undefined;
    const status = error.response?.status;

    // Refresh endpoint'ning o'zida 401 — qayta urinma
    const isRefreshCall = original?.url?.includes("/auth/refresh");
    const isLoginCall = original?.url?.includes("/auth/login");

    if (status === 401 && original && !original._retry && !isRefreshCall && !isLoginCall) {
      original._retry = true;
      const newToken = await getOrCreateRefresh();
      if (newToken) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>)["Authorization"] = `Bearer ${newToken}`;
        return api.request(original);
      }
      // Refresh ham 401 → tozalash (interceptor tashqarida router redirect qiladi)
      useAuthStore.getState().clear();
    }

    return Promise.reject(error);
  },
);
